"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const CheckCircleIcon = () => (
	<span className="mr-2 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-[#59ad1f] text-white sm:size-5">
		<svg
			viewBox="0 0 24 24"
			className="size-3 sm:size-3.5"
			aria-hidden="true"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M6 12.4L10.1 16.5L18.5 7.5"
				stroke="currentColor"
				strokeWidth="3"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	</span>
);

export interface SheetMetalFabricationIntroProps {
	/** next-intl 命名空间，需包含 title、introDescription、imageAlt、advantages*、advantage1/2* */
	translationNamespace?: string;
	/** 左侧图片路径 */
	imageSrc: string;
}

/**
 * Sheet Metal Fabrication 介绍区块：顶部居中标题+简介，底部左图右 Advantages（文案来自翻译文件）
 */
export default function SheetMetalFabricationIntro({
	translationNamespace = "sheetMetalFabricationIntro",
	imageSrc,
}: SheetMetalFabricationIntroProps) {
	const t = useTranslations(translationNamespace);

	const advantages = [
		{ title: t("advantage1Title"), description: t("advantage1Desc") },
		{ title: t("advantage2Title"), description: t("advantage2Desc") },
	];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("introDescription")}</p>
				</div>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-10">
					<div className="w-full lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/3] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[400px]">
							<Image
								src={imageSrc}
								alt={t("imageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>

					<div className="flex w-full min-w-0 flex-1 flex-col justify-center">
						<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-themePrimary400">
							{t("advantagesLabel")}
						</p>
						<h3 className="mb-6 text-lg font-bold text-black sm:text-xl md:text-2xl">
							{t("advantagesTitle")}
						</h3>
						<ul className="space-y-6">
							{advantages.map((item, index) => (
								<li key={index}>
									<p className="mb-2 flex items-center text-base font-semibold text-black sm:text-lg">
										<CheckCircleIcon />
										{item.title}
									</p>
									<p className="text-sm leading-relaxed text-black md:text-base">{item.description}</p>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
